import type { Pcl, Oss } from '../../index';
import { logger } from '@tuic/logger';

export type StageAtPort = Pcl.Stage & {
  date: string;
  portCode: string;
  name: string;
};
export type StageAtSea = Pcl.Stage & {
  date: string;
};
export type Stage = StageAtPort | StageAtSea;

export type StageWithMeta = {
  stage: Pcl.Stage & {
    date: string;
  };
  isDayAtSea: boolean;
  previousStop: Pcl.Stage | undefined;
  nextStop: Pcl.Stage | undefined;
};

export type StageMetaAtPort = {
  isDayAtSea: false;
  stage: StageAtPort;
  previousStop: Pcl.Stage | undefined;
  nextStop: Pcl.Stage | undefined;
};
export type ComposedStage = {
  stageMeta: StageMetaAtPort;
  ossIds: string[];
  ossMatch: Required<Oss.FilterItem>;
};

function stageIsDayAtSea(stage: Pcl.Stage): stage is StageAtSea {
  return !stage.portCode;
}

/**
 * Sorts Stages from earliest to latest arrival date
 */
export function sortStages(stages: Pcl.Stage[]) {
  const sortIndex = (stage: Pcl.Stage): number => {
    return stage.date ? new Date(stage.date).getTime() : Infinity;
  };

  return stages.sort((stageA, stageB) => sortIndex(stageA) - sortIndex(stageB));
}

/**
 * Prepare Stages for easier consumption
 */
export function sortedStagesWithMeta(stages: Pcl.Stage[]): StageWithMeta[] {
  return sortStages(stages)
    .filter((stage): stage is Stage => {
      if (!stage.date) {
        logger.warn(`Cannot consume trip stage '${stage.name}' without a date`);
      }
      return !!stage.date;
    })
    .map((stage, stageIndex): StageWithMeta => {
      return {
        stage: stage,
        isDayAtSea: stageIsDayAtSea(stage),
        previousStop: stages
          ?.slice(0, stageIndex)
          .reverse()
          .find(nextStage => !stageIsDayAtSea(nextStage)),
        nextStop: stages
          ?.slice(stageIndex + 1)
          .find(prevStage => !stageIsDayAtSea(prevStage)),
      };
    });
}

/**
 * Removes chained ports
 */
export function sortPortsComposed(stages: Pcl.Stage[]) {
  return sortedStagesWithMeta(stages).reduce<typeof stages>(
    (aggr, { previousStop, stage }) => {
      if (previousStop?.portCode !== stage?.portCode) {
        aggr.push(stage);
      }
      return aggr;
    },
    [],
  );
}

/**
 * Same as `sortedStagesWithMeta` but composes duplicate ports
 * This function also helps to bind stages to excursion-options provided
 * by OSS API
 */
export function sortPortsComposedForExcursions(
  stages: Pcl.Stage[],
  excursionOptions: Oss.FilterItem[] | undefined,
): ComposedStage[] {
  if (!excursionOptions) return [];

  // This binds every port(stage) of the trip to an oss match.
  // => Filters every port that has no oss match
  const stagesAtPort = sortedStagesWithMeta(stages)
    .filter((stageMeta): stageMeta is StageMetaAtPort => !stageMeta.isDayAtSea)
    .map<[StageMetaAtPort, Required<Oss.FilterItem> | undefined]>(stageMeta => {
      const stage = stageMeta.stage;

      function findExcursionMatch(
        option: Oss.FilterItem,
      ): option is Required<Oss.FilterItem> {
        const idSplit = option.id?.split(':');
        if (idSplit?.length === 4) {
          const [portCode, tripCode, shipCode, stageDate] = idSplit;
          // excursion on that 'port' and on that 'date' is matched
          return (
            !!option.name &&
            stage.date === stageDate &&
            portCode === stage.portCode
          );
        }
        return false;
      }
      const ossMatch = excursionOptions?.find(findExcursionMatch);
      return [stageMeta, ossMatch];
    })
    .filter(
      (
        alignedData,
      ): alignedData is [StageMetaAtPort, Required<Oss.FilterItem>] =>
        !!alignedData[1],
    );

  const results: ComposedStage[] = [];
  let localGroupIndex = 0;
  for (let i = 0; i < stagesAtPort.length; i++) {
    const [stageMeta, ossMatch] = stagesAtPort[i];

    // initialize group
    if (!results[localGroupIndex]) {
      results[localGroupIndex] = {
        ossIds: [],
        ossMatch,
        stageMeta,
      };
    }

    results[localGroupIndex].ossIds.push(ossMatch?.id);
    if (stageMeta.stage.portCode !== stageMeta.nextStop?.portCode) {
      localGroupIndex++;
    }
  }

  return results;
}
